// src/pages/Trainer/CourseManagement/Components/CreateCourse/OfferingsSessionsTab.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Calendar, MapPin, Users, Clock } from 'lucide-react';
import {
  createCourseOffering,
  getCourseOfferings,
  updateCourseOffering,
  deleteCourseOffering,
  createCourseSession,
  getCourseSessions,
  updateCourseSession,
  deleteCourseSession,
  type CourseOffering,
  type CourseSession,
} from '@/services/AdminService/AdminCourseManagementService';
import './CourseCreationTabs.css';

interface OfferingsSessionsTabProps {
  courseId?: number;
}

const OfferingsSessionsTab: React.FC<OfferingsSessionsTabProps> = ({ courseId }) => {
  const [offerings, setOfferings] = useState<CourseOffering[]>([]);
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [activeTab, setActiveTab] = useState<'offerings' | 'sessions'>('offerings');
  const [showOfferingForm, setShowOfferingForm] = useState(false);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [offeringFormData, setOfferingFormData] = useState<Partial<CourseOffering> | null>(null);
  const [sessionFormData, setSessionFormData] = useState<Partial<CourseSession> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (courseId) {
      loadOfferings();
      loadSessions();
    }
  }, [courseId]);

  const loadOfferings = async () => {
    if (!courseId) return;

    setLoading(true);
    try {
      const result = await getCourseOfferings(courseId);
      if (result.success && result.data) {
        setOfferings(result.data);
      } else {
        setError(result.error?.message || 'Failed to load offerings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load offerings');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    if (!courseId) return;

    try {
      const result = await getCourseSessions(courseId);
      if (result.success && result.data) {
        setSessions(result.data);
      }
    } catch (err: any) {
      console.error('Failed to load sessions:', err);
    }
  };

  const handleSaveOffering = async () => {
    if (!courseId || !offeringFormData) return;

    if (!offeringFormData.name || !offeringFormData.startDate || !offeringFormData.endDate || !offeringFormData.totalSeats) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (offeringFormData.id) {
        // Update
        const result = await updateCourseOffering(offeringFormData.id, offeringFormData);
        if (result.success) {
          await loadOfferings();
          setSuccess('Offering updated successfully');
          setShowOfferingForm(false);
          setOfferingFormData(null);
        } else {
          setError(result.error?.message || 'Failed to update offering');
        }
      } else {
        // Create
        const result = await createCourseOffering(courseId, offeringFormData as CourseOffering);
        if (result.success) {
          await loadOfferings();
          setSuccess('Offering created successfully');
          setShowOfferingForm(false);
          setOfferingFormData(null);
        } else {
          setError(result.error?.message || 'Failed to create offering');
        }
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save offering');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOffering = async (offeringId: number) => {
    if (!window.confirm('Are you sure you want to delete this offering?')) return;

    setLoading(true);
    try {
      const result = await deleteCourseOffering(offeringId);
      if (result.success) {
        await loadOfferings();
        setSuccess('Offering deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error?.message || 'Failed to delete offering');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete offering');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSession = async () => {
    if (!courseId || !sessionFormData) return;

    if (!sessionFormData.name || !sessionFormData.sessionDate || !sessionFormData.startTime || !sessionFormData.endTime) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (sessionFormData.id) {
        // Update
        const result = await updateCourseSession(sessionFormData.id, sessionFormData);
        if (result.success) {
          await loadSessions();
          setSuccess('Session updated successfully');
          setShowSessionForm(false);
          setSessionFormData(null);
        } else {
          setError(result.error?.message || 'Failed to update session');
        }
      } else {
        // Create
        const result = await createCourseSession(courseId, sessionFormData as CourseSession);
        if (result.success) {
          await loadSessions();
          setSuccess('Session created successfully');
          setShowSessionForm(false);
          setSessionFormData(null);
        } else {
          setError(result.error?.message || 'Failed to create session');
        }
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save session');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    setLoading(true);
    try {
      const result = await deleteCourseSession(sessionId);
      if (result.success) {
        await loadSessions();
        setSuccess('Session deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error?.message || 'Failed to delete session');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete session');
    } finally {
      setLoading(false);
    }
  };

  if (!courseId) {
    return (
      <div className="tab-content-wrapper">
        <div className="info-message">
          <p>Please save the basic course information first before managing offerings and sessions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content-wrapper">
      <div className="tab-header">
        <h2>Course Offerings & Sessions</h2>
        <p className="tab-subtitle">
          Manage training schedules, offerings, and sessions for instructor-led training courses. <em>(Optional)</em>
        </p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-btn">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <p>{success}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation" style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('offerings')}
          className={activeTab === 'offerings' ? 'tab-btn active' : 'tab-btn'}
        >
          <Calendar size={18} />
          Offerings ({offerings.length})
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={activeTab === 'sessions' ? 'tab-btn active' : 'tab-btn'}
        >
          <Clock size={18} />
          Sessions ({sessions.length})
        </button>
      </div>

      {/* Offerings Tab */}
      {activeTab === 'offerings' && (
        <div>
          <button
            onClick={() => {
              setOfferingFormData({
                name: '',
                startDate: '',
                endDate: '',
                timezone: 'EST',
                totalSeats: 30,
                enableWaitlist: false,
              });
              setShowOfferingForm(true);
            }}
            className="btn-primary"
            style={{ marginBottom: '20px' }}
          >
            <Plus size={18} />
            Add Offering
          </button>

          {offerings.length === 0 ? (
            <div className="empty-state">
              <p>No offerings created yet. Add your first offering to schedule training sessions.</p>
            </div>
          ) : (
            <div className="offerings-grid">
              {offerings.map((offering) => (
                <div key={offering.id} className="offering-card">
                  <div className="offering-header">
                    <h3>{offering.name}</h3>
                    <span className={`status-badge status-${offering.status?.toLowerCase()}`}>
                      {offering.status}
                    </span>
                  </div>
                  <div className="offering-details">
                    <div className="detail-item">
                      <Calendar size={16} />
                      <span>{new Date(offering.startDate).toLocaleDateString()} - {new Date(offering.endDate).toLocaleDateString()}</span>
                    </div>
                    {offering.location && (
                      <div className="detail-item">
                        <MapPin size={16} />
                        <span>{offering.location}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <Users size={16} />
                      <span>{offering.availableSeats || offering.totalSeats}/{offering.totalSeats} seats available</span>
                    </div>
                  </div>
                  <div className="offering-actions">
                    <button
                      onClick={() => {
                        setOfferingFormData(offering);
                        setShowOfferingForm(true);
                      }}
                      className="btn-icon"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => offering.id && handleDeleteOffering(offering.id)}
                      className="btn-icon btn-danger"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && (
        <div>
          <button
            onClick={() => {
              setSessionFormData({
                name: '',
                sessionDate: '',
                startTime: '',
                endTime: '',
                deliveryMethod: 'Virtual',
                maxCapacity: 30,
              });
              setShowSessionForm(true);
            }}
            className="btn-primary"
            style={{ marginBottom: '20px' }}
          >
            <Plus size={18} />
            Add Session
          </button>

          {sessions.length === 0 ? (
            <div className="empty-state">
              <p>No sessions scheduled yet. Add your first training session.</p>
            </div>
          ) : (
            <div className="sessions-list">
              {sessions.map((session) => (
                <div key={session.id} className="session-card">
                  <div className="session-header">
                    <h3>{session.name}</h3>
                    <span className={`badge badge-${session.deliveryMethod?.toLowerCase()}`}>
                      {session.deliveryMethod}
                    </span>
                  </div>
                  <div className="session-details">
                    <div className="detail-row">
                      <Calendar size={16} />
                      <span>{new Date(session.sessionDate).toLocaleDateString()}</span>
                      <Clock size={16} />
                      <span>{session.startTime} - {session.endTime}</span>
                    </div>
                    {session.instructorName && (
                      <div className="detail-row">
                        <span>Instructor: {session.instructorName}</span>
                      </div>
                    )}
                    {session.virtualMeetingLink && (
                      <div className="detail-row">
                        <a href={session.virtualMeetingLink} target="_blank" rel="noopener noreferrer" className="link">
                          Join Meeting
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="session-actions">
                    <button
                      onClick={() => {
                        setSessionFormData(session);
                        setShowSessionForm(true);
                      }}
                      className="btn-icon"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => session.id && handleDeleteSession(session.id)}
                      className="btn-icon btn-danger"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Offering Form Modal */}
      {showOfferingForm && offeringFormData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{offeringFormData.id ? 'Edit Offering' : 'Add Offering'}</h3>
              <button onClick={() => setShowOfferingForm(false)} className="btn-icon">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Offering Name *</label>
                  <input
                    type="text"
                    value={offeringFormData.name}
                    onChange={(e) => setOfferingFormData({ ...offeringFormData, name: e.target.value })}
                    placeholder="e.g., Spring 2024 Batch"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={offeringFormData.startDate}
                    onChange={(e) => setOfferingFormData({ ...offeringFormData, startDate: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={offeringFormData.endDate}
                    onChange={(e) => setOfferingFormData({ ...offeringFormData, endDate: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Total Seats *</label>
                  <input
                    type="number"
                    value={offeringFormData.totalSeats}
                    onChange={(e) => setOfferingFormData({ ...offeringFormData, totalSeats: parseInt(e.target.value) })}
                    min="1"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <select
                    value={offeringFormData.timezone}
                    onChange={(e) => setOfferingFormData({ ...offeringFormData, timezone: e.target.value })}
                    className="form-select"
                  >
                    <option value="EST">EST</option>
                    <option value="PST">PST</option>
                    <option value="CST">CST</option>
                    <option value="MST">MST</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={offeringFormData.enableWaitlist}
                      onChange={(e) => setOfferingFormData({ ...offeringFormData, enableWaitlist: e.target.checked })}
                    />
                    <span style={{ marginLeft: '8px' }}>Enable Waitlist</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleSaveOffering} disabled={loading} className="btn-primary">
                <Save size={18} />
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setShowOfferingForm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Form Modal */}
      {showSessionForm && sessionFormData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{sessionFormData.id ? 'Edit Session' : 'Add Session'}</h3>
              <button onClick={() => setShowSessionForm(false)} className="btn-icon">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Session Name *</label>
                  <input
                    type="text"
                    value={sessionFormData.name}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, name: e.target.value })}
                    placeholder="e.g., Week 1 - Introduction"
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Session Date *</label>
                  <input
                    type="date"
                    value={sessionFormData.sessionDate}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, sessionDate: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time *</label>
                  <input
                    type="time"
                    value={sessionFormData.startTime}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, startTime: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>End Time *</label>
                  <input
                    type="time"
                    value={sessionFormData.endTime}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, endTime: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Delivery Method *</label>
                  <select
                    value={sessionFormData.deliveryMethod}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, deliveryMethod: e.target.value as any })}
                    className="form-select"
                  >
                    <option value="Virtual">Virtual</option>
                    <option value="In-Person">In-Person</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Max Capacity</label>
                  <input
                    type="number"
                    value={sessionFormData.maxCapacity}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, maxCapacity: parseInt(e.target.value) })}
                    min="1"
                    className="form-input"
                  />
                </div>
              </div>
              {(sessionFormData.deliveryMethod === 'Virtual' || sessionFormData.deliveryMethod === 'Hybrid') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Virtual Meeting Link</label>
                    <input
                      type="url"
                      value={sessionFormData.virtualMeetingLink || ''}
                      onChange={(e) => setSessionFormData({ ...sessionFormData, virtualMeetingLink: e.target.value })}
                      placeholder="https://zoom.us/j/123456789"
                      className="form-input"
                    />
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={sessionFormData.description || ''}
                    onChange={(e) => setSessionFormData({ ...sessionFormData, description: e.target.value })}
                    placeholder="Session description"
                    className="form-textarea"
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleSaveSession} disabled={loading} className="btn-primary">
                <Save size={18} />
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => setShowSessionForm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && !showOfferingForm && !showSessionForm && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default OfferingsSessionsTab;
